/**
 * Split Hero
 * 
 * Image on one side, content on the other.
 * Great for corporate and professional sites.
 */

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { heroImageService } from "@/services/heroImageService";
import { queryKeys } from "@/lib/query-config";
import { useState, useEffect } from "react";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const HeroSplit = () => {
  const { features, data } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;
  
  const heroImageEnabled = (data?.features as Record<string, unknown>)?.heroImage !== false;

  // Fetch active hero images
  const { data: heroImages, isLoading: heroImagesLoading } = useQuery({
    queryKey: queryKeys.heroImages.active(),
    queryFn: async () => {
      const result = await heroImageService.getActiveHeroImages();
      if (!result.success) return [];
      return result.data;
    },
    enabled: heroImageEnabled,
    staleTime: 0, // Always fetch fresh data to show latest hero image
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Use first hero image if available, otherwise fallback to default
  const currentHeroImage = heroImagesLoading
    ? null
    : heroImages && heroImages.length > 0
    ? heroImages[0].photo?.image_url
    : null;

  // Add cache-busting to image URL to prevent old image from showing
  const imageUrlWithCacheBust = currentHeroImage && heroImages?.[0]?.id && heroImages?.[0]?.updated_at
    ? `${currentHeroImage}${currentHeroImage.includes('?') ? '&' : '?'}v=${heroImages[0].id}_${heroImages[0].updated_at}`
    : currentHeroImage;

  // Track image loading state to prevent showing old image
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageKey, setCurrentImageKey] = useState<string | null>(null);

  // Reset loading state when image URL changes
  useEffect(() => {
    if (imageUrlWithCacheBust !== currentImageKey) {
      setImageLoaded(false);
      setCurrentImageKey(imageUrlWithCacheBust);
    }
  }, [imageUrlWithCacheBust, currentImageKey]);

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
    small: 'min-h-[40vh]',
  };

  return (
    <section className={cn(
      "relative overflow-hidden bg-background",
      heightClasses[heroConfig.height]
    )}>
      <div className="max-w-7xl mx-auto h-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full">
          {/* Content Side */}
          <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
            <div className="max-w-xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                {hero.title}
                <span className="block text-heritage mt-2">
                  {hero.highlightedText}
                </span>
              </h1>
              
              <div className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                <RichTextDisplay content={hero.subtitle || ''} prose={false} />
              </div>

              {/* CTA Buttons */}
              {heroConfig.showCTA && (
                <div className="flex flex-col sm:flex-row gap-4">
                  {features.membership.enabled && (
                    <Button variant="heritage" size="lg" asChild className="group">
                      <Link to="/membership">
                        <Users className="w-5 h-5" />
                        Join Us
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  )}
                  
                  {features.donation.enabled && (
                    <Button variant="donate" size="lg" asChild className="group">
                      <Link to="/donate">
                        <Heart className="w-5 h-5" />
                        Support
                      </Link>
                    </Button>
                  )}
                  
                  {features.events.enabled && (
                    <Button variant="culture" size="lg" asChild className="group">
                      <Link to="/events">
                        <Calendar className="w-5 h-5" />
                        Events
                      </Link>
                    </Button>
                  )}

                  {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
                    <Button variant="heritage" size="lg" asChild className="group">
                      <Link to="/about-us">
                        Learn More
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  )}
                </div>
              )}

              {/* Statistics */}
              {heroConfig.showStats && hero.showStats && hero.stats.length > 0 && (
                <div className="mt-12 grid grid-cols-3 gap-6">
                  {hero.stats.map((stat, index) => (
                    <div key={index} className="text-center lg:text-left">
                      <div className="text-2xl md:text-3xl font-bold text-heritage">{stat.value}</div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Side */}
          <div className="relative order-1 lg:order-2 min-h-[300px] lg:min-h-0">
            {heroImageEnabled ? (
              <>
                {/* Hide image until loaded to prevent showing old cached image */}
                {imageUrlWithCacheBust && (
                  <div className={cn(
                    "absolute inset-0 transition-opacity duration-300",
                    imageLoaded ? "opacity-100" : "opacity-0"
                  )}>
                    <img
                      src={imageUrlWithCacheBust}
                      alt={heroImages && heroImages.length > 0 && heroImages[0].title 
                        ? heroImages[0].title 
                        : `${siteConfig.organizationName} community`}
                      className="w-full h-full object-cover"
                      key={`hero-${heroImages?.[0]?.id || 'default'}-${heroImages?.[0]?.updated_at || 'default'}`}
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageLoaded(true)} // Show something even if image fails
                    />
                  </div>
                )}
                {/* Loading/fallback background */}
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-gradient-to-br from-heritage via-culture to-accent animate-pulse" />
                )}
                <div 
                  className="absolute inset-0 bg-heritage/20 pointer-events-none"
                  style={{ opacity: heroConfig.overlayOpacity / 100 }}
                />
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-heritage via-culture to-accent" />
            )}
            
            {/* Decorative element */}
            <div className="absolute -left-10 top-1/2 -translate-y-1/2 w-20 h-20 bg-heritage/20 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSplit;


